import { CREDIT_PACKS, type OfferedCreditPack } from 'fym-shared/src/music24/creditPacks';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { MUSIC_VIDEO_ERROR_CODES } from 'fym-shared/src/music24/musicVideoApi';
import { coverErrorStatus } from '../../../coverEditor/doc/coverEditorErrors';
import { directedErrorCode, directedErrorReason } from '../../directedErrors';

/** The credits that the balance lacks for the price; 0 when it is enough or has no limit. Pure. */
export function missingCredits(balance: number | null, priceCredits: number): number {
  return balance === null ? 0 : Math.max(0, priceCredits - balance);
}

/**
 * The pack that the price step selects first (design f2): the smallest pack that covers the
 * missing credits, else the biggest pack. Pure.
 */
export function packForMissing(missing: number, packs: readonly OfferedCreditPack[] = CREDIT_PACKS): OfferedCreditPack {
  const sorted = [...packs].sort((a, b) => a.credits - b.credits);
  return sorted.find((pack) => pack.credits >= missing) ?? (sorted[sorted.length - 1] as OfferedCreditPack);
}

/**
 * What the price step offers (QA 2026-10-28, skpr2-1):
 * - `aiPlan`: the plan makes no AI footage (`canMakeAiFootage`: Free, the trial). The video cannot
 *   be made; the step offers the plans, never a pack.
 * - `packs`: the owner of a paid plan on the web is short of credits: the credit packs.
 * - `plan`: Free or the trial is short: the plans (the server refuses a pack, `M24_PACKS_NEED_PLAN`).
 * - `none`: enough credits, a member (the owner note) or a native app's web view (no purchase).
 * Pure.
 */
export type PriceOffer = 'aiPlan' | 'packs' | 'plan' | 'none';

export function priceOffer(input: {
  canMakeAiFootage: boolean;
  /** The plan of the credits answer; null while it loads. */
  plan: Music24PlanTier | null;
  isShort: boolean;
  isOwner: boolean;
  isNativeEmbed: boolean;
}): PriceOffer {
  if (!input.canMakeAiFootage) {
    return 'aiPlan';
  }
  if (!input.isShort || !input.isOwner || input.isNativeEmbed) {
    return 'none';
  }
  if (input.plan === null) {
    return 'none';
  }
  return input.plan === 'free' || input.plan === 'trial' ? 'plan' : 'packs';
}

/** Why "Make the video" was refused (design f3). Every refusal charges nothing. */
export type ConfirmRefusal =
  /** 409 `M24_DIRECTED_STALE`: the script changed after the price showed. */
  | 'stale'
  /** 409 `M24_DIRECTED_BUSY`: another directed video of the workspace is made now. */
  | 'otherMaking'
  /** 409 `M24_DIRECTED_BUSY` reason `render`: a music video of this song renders now. */
  | 'songRendering'
  /** 503: the monthly budget of the beta is spent (owner ruling: "busy, try again later"). */
  | 'budget'
  /** 402 `M24_LIMIT_QUOTA`: the credits are not enough (the app opens the packs). */
  | 'noCredits'
  | 'failed';

/** Maps the refusal of `POST …/confirm`. Pure. */
export function confirmRefusalOf(error: unknown): ConfirmRefusal {
  const code = directedErrorCode(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.directedStale) {
    return 'stale';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.directedBusy) {
    return directedErrorReason(error) === 'render' ? 'songRendering' : 'otherMaking';
  }
  if (code === 'M24_LIMIT_QUOTA') {
    return 'noCredits';
  }
  return coverErrorStatus(error) === 503 ? 'budget' : 'failed';
}
